import { createFileRoute, redirect } from '@tanstack/react-router';
import { settingsQueryOptions } from '@/api/services/settings';
import { assertAuth0ExtParamsLength, type CustomAuthorizationParams } from '@/helpers/auth';
import { AUTH_REDIRECT_URL, BASE_URL } from '@/helpers/url';
import { head } from '@/router/Head';
import { loginSearchSchema } from '@/router/utils';
import { UnavailableAuth0Context } from '../__root.hooks';

export const Route = createFileRoute('/(auth)/login')({
  validateSearch: loginSearchSchema,
  staticData: {
    title: 'Logging in ...',
    domain: 'auth',
  },
  beforeLoad: async ({
    context: { auth, queryClient },
    // A redirection with SSO params happens when a normal login connection was used instead, and the API + UI needs to redirect /login with these sso params.
    search: { aerr: authError, sso_connection: loginConnection, sso_login_hint: loginHint, return_to: returnTo },
  }) => {
    if (auth === UnavailableAuth0Context) {
      // Discriminate type to satisfy the compiler: this case should never happen because the route is only rendered when Auth0 context is available
      throw new Error('Auth0 context is not available');
    }
    if (auth.isAuthenticated) {
      throw redirect({ href: AUTH_REDIRECT_URL, replace: true });
    }

    const authParams: CustomAuthorizationParams = {
      'ext-back_link': BASE_URL.toString(),
      'ext-cdn': window.__QDRANT_CLOUD__.qdrant_cdn_url.replace(/\/+$/, ''),
      'ext-home-url': BASE_URL.origin,
      'ext-onetrust-domain-id': window.__QDRANT_CLOUD__.onetrust_domain_id,
      'ext-segment-write-key': window.__QDRANT_CLOUD__.segment.write_key,
    };

    if (loginConnection) {
      /*
       * connection: SSO connection name/identifier that will be used to redirect the user to the SSO login page
       * login_hint: SSO email address to fill-in the email field in the signup form
       */
      authParams.connection = loginConnection;
      authParams.login_hint = loginHint;
    }
    /*
     * The `authError` query parameter is set by the Auth0 SDK when an error occurs during authentication.
     * It could also happen during the silent refreshing/retrieval of the access token.
     * See: https://auth0.com/docs/api/authentication?javasaddiaddovcript#:~:text=Standard%20Error%20Responses
     */
    if (authError) {
      authParams['ext-aerr'] = authError;
    }

    // The `incident_mode` custom parameter is set when INCIDENT_MODE is active via /settings.json
    const { incident } = await queryClient.fetchQuery(settingsQueryOptions);
    if (incident) {
      authParams['ext-incident_mode'] = 'true';
    }

    assertAuth0ExtParamsLength(authParams);

    await auth.loginWithRedirect({
      appState: { returnTo },
      authorizationParams: {
        audience: window.__QDRANT_CLOUD__.auth0.audience,
        scope: window.__QDRANT_CLOUD__.auth0.scope,
        redirect_uri: AUTH_REDIRECT_URL,
        screen_hint: 'login',
        ...authParams,
      },
    });
  },
  head,
});
